import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input, Select } from "@sagot/ui";
import { STAGE_NAME_MAX, type StageDraft, type StageOutcome } from "./lead-logic";

interface StageDraftRowProps {
  draft: StageDraft;
  index: number;
  count: number;
  onChange: (next: StageDraft | null) => void;
  onMove: (direction: -1 | 1) => void;
}

const OUTCOMES: readonly StageOutcome[] = ["open", "won", "lost"];

/** One editable pipeline stage: name, outcome (open / won / lost), reorder, remove. */
export function StageDraftRow({ draft, index, count, onChange, onMove }: StageDraftRowProps) {
  const { t } = useTranslation();
  const name = draft.name || t("engage.leads.stagesDialog.newStage");
  return (
    <li className="flex flex-wrap items-center gap-2 rounded-md border border-line p-2" data-testid="stage-draft">
      <Input
        className="h-9 min-w-0 flex-1"
        aria-label={t("engage.leads.stagesDialog.name", { n: index + 1 })}
        value={draft.name}
        maxLength={STAGE_NAME_MAX}
        onChange={(event) => {
          onChange({ ...draft, name: event.target.value });
        }}
      />
      <Select
        className="h-9 w-28"
        aria-label={t("engage.leads.stagesDialog.outcome", { name })}
        value={draft.outcome}
        onChange={(event) => {
          const outcome = OUTCOMES.find((o) => o === event.target.value) ?? "open";
          onChange({ ...draft, outcome });
        }}
      >
        {OUTCOMES.map((outcome) => (
          <option key={outcome} value={outcome}>
            {t(`engage.leads.stagesDialog.${outcome}`)}
          </option>
        ))}
      </Select>
      <span className="w-16 text-xs text-fg-subtle">{t("engage.leads.stagesDialog.leads", { count: draft.leadCount })}</span>
      <div className="flex">
        <Button variant="ghost" size="sm" iconOnly aria-label={t("engage.leads.stagesDialog.moveUp", { name })} disabled={index === 0} onClick={() => { onMove(-1); }}>
          <Icon name="chevron-up" />
        </Button>
        <Button variant="ghost" size="sm" iconOnly aria-label={t("engage.leads.stagesDialog.moveDown", { name })} disabled={index === count - 1} onClick={() => { onMove(1); }}>
          <Icon name="chevron-down" />
        </Button>
        <Button variant="ghost" size="sm" iconOnly aria-label={t("engage.leads.stagesDialog.remove", { name })} onClick={() => { onChange(null); }}>
          <Icon name="trash" />
        </Button>
      </div>
    </li>
  );
}
